import apiClient from "./axiosConfig";

export const RUNTIME_LOCATION_SYNC_KEY = "dople_runtime_location_sync_v1";
export const RUNTIME_LOCATION_CURRENT_KEY = "dople_runtime_location_current_v1";
// Runtime location is scoped to this browser visit by sessionStorage. Once it
// has been captured, it remains immediately usable while a new observation is
// refreshed every ten minutes. A new browser session must capture it again.
export const RUNTIME_LOCATION_REFRESH_MS = 10 * 60 * 1000;

const now = () => Date.now();

const safeReadJson = (key) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const safeWriteJson = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Session-only affordance.
  }
};

export function readRuntimeLocationSync() {
  return safeReadJson(RUNTIME_LOCATION_SYNC_KEY) || {};
}

export function writeRuntimeLocationSync(payload = {}) {
  safeWriteJson(RUNTIME_LOCATION_SYNC_KEY, { ...payload, synced_at: now() });
}

export function readRuntimeLocationCurrent() {
  const payload = safeReadJson(RUNTIME_LOCATION_CURRENT_KEY);
  if (!payload || typeof payload !== "object") return null;
  const observedAt = Number(payload.observed_at_ms || payload.synced_at || 0);
  if (!observedAt) return null;
  if (payload.latitude == null || payload.longitude == null) return null;
  return payload;
}

export function writeRuntimeLocationCurrent(payload = {}) {
  if (payload.latitude == null || payload.longitude == null) return;
  safeWriteJson(RUNTIME_LOCATION_CURRENT_KEY, {
    ...payload,
    observed_at_ms: now(),
    synced_at: now(),
  });
}

export function clearRuntimeLocationSession() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(RUNTIME_LOCATION_SYNC_KEY);
    window.sessionStorage.removeItem(RUNTIME_LOCATION_CURRENT_KEY);
  } catch {
    // ignore
  }
}

export function runtimeLocationForRequest() {
  const current = readRuntimeLocationCurrent();
  if (!current) return null;
  return {
    latitude: current.latitude,
    longitude: current.longitude,
    accuracy: current.accuracy,
    source: current.source || "browser_device",
    permission_state: current.permission_state || "",
    observed_at_ms: current.observed_at_ms || current.synced_at || now(),
  };
}

export async function syncBrowserRuntimeLocation({ pathname = "", captureReason = "", force = false } = {}) {
  if (
    typeof window === "undefined" ||
    !window.isSecureContext ||
    !navigator.geolocation
  ) {
    writeRuntimeLocationSync({ state: "unavailable", reason: "geolocation_unavailable" });
    return { state: "unavailable" };
  }

  const prior = readRuntimeLocationSync();
  const current = readRuntimeLocationCurrent();
  const recentlyAttempted = now() - Number(prior.synced_at || 0) < RUNTIME_LOCATION_REFRESH_MS;
  if (!force && recentlyAttempted && (current || ["denied", "unavailable"].includes(prior.state))) {
    return prior;
  }

  let permissionState = "unknown";
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: "geolocation" });
      permissionState = status?.state || permissionState;
      if (permissionState === "denied") {
        const denied = { state: "denied", permission_state: permissionState };
        writeRuntimeLocationSync(denied);
        window.dispatchEvent(new CustomEvent("dople:runtime-location", {
          detail: { location_unavailable: true, permission_state: permissionState },
        }));
        return denied;
      }
    }
  } catch {
    // Some browsers do not expose geolocation through Permissions API.
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const onPosition = async (position) => {
        if (!position?.coords) {
          const unavailable = { state: "unavailable", permission_state: permissionState };
          writeRuntimeLocationSync(unavailable);
          finish(unavailable);
          return;
        }
        const requestPayload = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          source: "browser_device",
          permission_state: permissionState,
          capture_reason: captureReason || `app_route:${pathname || ""}`,
        };
        writeRuntimeLocationCurrent(requestPayload);
        window.dispatchEvent(new CustomEvent("dople:runtime-location", {
          detail: {
            location_captured: true,
            local_location_cached: true,
            accuracy_m: position.coords.accuracy,
          },
        }));
        try {
          const res = await apiClient.post("/api/runtime-location", requestPayload);
          const synced = {
            state: "synced",
            permission_state: permissionState,
            accuracy_m: position.coords.accuracy,
          };
          writeRuntimeLocationSync(synced);
          window.dispatchEvent(new CustomEvent("dople:runtime-location", { detail: res.data || {} }));
          finish(synced);
        } catch (err) {
          console.warn("[runtimeLocation] Runtime location sync failed:", err);
          const localOnly = {
            state: "synced_local_only",
            permission_state: permissionState,
            accuracy_m: position.coords.accuracy,
          };
          writeRuntimeLocationSync(localOnly);
          window.dispatchEvent(new CustomEvent("dople:runtime-location", {
            detail: { location_context_changed: true, local_only_location_cached: true },
          }));
          finish(localOnly);
        }
    };
    const fail = (error) => {
        const unavailable = {
          state: error?.code === 1 ? "denied" : "unavailable",
          permission_state: permissionState,
          error_code: error?.code || 0,
          reason: error?.code === 3 ? "location_timeout" : error?.code === 2 ? "position_unavailable" : "permission_denied",
        };
        writeRuntimeLocationSync(unavailable);
        window.dispatchEvent(new CustomEvent("dople:runtime-location", {
          detail: {
            location_unavailable: true,
            permission_state: permissionState,
            error_code: error?.code || 0,
          },
        }));
        finish(unavailable);
    };
    const retryOrFail = (error) => {
      if (error?.code === 1) {
        fail(error);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        onPosition,
        fail,
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60 * 1000 }
      );
    };
    navigator.geolocation.getCurrentPosition(
      onPosition,
      retryOrFail,
      { enableHighAccuracy: false, timeout: 7000, maximumAge: 0 }
    );
  });
}

export function waitForRuntimeLocationReady({ timeoutMs = 5200 } = {}) {
  if (typeof window === "undefined") return Promise.resolve({ state: "unavailable" });
  const current = readRuntimeLocationCurrent();
  if (current) return Promise.resolve({ state: "synced", current });
  const sync = readRuntimeLocationSync();
  if (["denied", "unavailable"].includes(sync.state)) {
    return Promise.resolve(sync);
  }
  if (!window.isSecureContext || !navigator.geolocation) {
    return Promise.resolve({ state: "unavailable" });
  }
  return new Promise((resolve) => {
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      window.removeEventListener("dople:runtime-location", onLocation);
      window.clearTimeout(timer);
      resolve(value || readRuntimeLocationSync() || { state: "timeout" });
    };
    const onLocation = (event) => {
      const detail = event?.detail || {};
      if (detail.location_unavailable) {
        finish({ state: "unavailable", ...detail });
        return;
      }
      finish({ state: "synced", ...detail });
    };
    const timer = window.setTimeout(() => finish({ state: "timeout" }), timeoutMs);
    window.addEventListener("dople:runtime-location", onLocation);
  });
}
